import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

import {
  Trash2Icon,
  HelpCircleIcon,
  PlayIcon,
  PauseIcon,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  ScrollTextIcon,
  UserRound,
} from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemLocked,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { driver } from "driver.js";
import "driver.js/dist/driver.css";

import ConfettiExplosion from "react-confetti-explosion";
import { WordArrayState, ActiveButton, SkillLevel } from "./lib/types";
import Slideshow from "./components/slideshow";
import { tour } from "./config/tour";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
import { defaultConfig } from "./lib/utils";

interface Props {
  isLoggedIn: boolean;
  email: string;
  displayName: string;
}

function App({ isLoggedIn, email, displayName }: Props) {
  const availableBasewords = [
    { value: "sign", label: "Sign", isLocked: false },
    { value: "form", label: "Form", isLocked: !isLoggedIn },
    { value: "know", label: "Know", isLocked: !isLoggedIn },
    { value: "tract", label: "Tract", isLocked: !isLoggedIn },
    { value: "port", label: "Port", isLocked: !isLoggedIn },
    { value: "rupt", label: "Rupt", isLocked: !isLoggedIn },
    { value: "ject", label: "Ject", isLocked: !isLoggedIn },
    { value: "spect", label: "Spect", isLocked: !isLoggedIn },
    { value: "auto", label: "Auto", isLocked: !isLoggedIn },
    { value: "geo", label: "Geo", isLocked: !isLoggedIn },
    { value: "bio", label: "Bio", isLocked: !isLoggedIn },
    { value: "graph", label: "Graph", isLocked: !isLoggedIn },
    { value: "photo", label: "Photo", isLocked: !isLoggedIn },
    { value: "hydr", label: "Hydr", isLocked: !isLoggedIn },
    { value: "bene", label: "Bene", isLocked: !isLoggedIn },
    { value: "dict", label: "Dict", isLocked: !isLoggedIn },
    { value: "fact", label: "Fact", isLocked: !isLoggedIn },
    { value: "struct", label: "Struct", isLocked: !isLoggedIn },
    { value: "phys", label: "Phys", isLocked: !isLoggedIn },
    { value: "phon", label: "Phon", isLocked: !isLoggedIn },
    { value: "vis", label: "Vis", isLocked: !isLoggedIn },
    { value: "fer", label: "Fer", isLocked: !isLoggedIn },
    { value: "dent", label: "Dent", isLocked: !isLoggedIn },
    { value: "cred", label: "Cred", isLocked: !isLoggedIn },
    { value: "fine", label: "Fine", isLocked: !isLoggedIn },
    { value: "aer", label: "Aer", isLocked: !isLoggedIn },
    { value: "path", label: "Path", isLocked: !isLoggedIn },
    { value: "aud", label: "Aud", isLocked: !isLoggedIn },
    { value: "text", label: "Text", isLocked: !isLoggedIn },
    { value: "press", label: "Press", isLocked: !isLoggedIn },
  ];

  const [baseWord, setBaseWord] = useState<string>(
    defaultConfig.baseWord.value
  );
  const [config, setConfig] = useState<any>(defaultConfig);
  useEffect(() => {
    import(`./config/basewords/${baseWord}`).then((module) => {
      setConfig(module.config);
      setSkillLevel(`L${module.config.settings.defaultLevel}` as SkillLevel);
      setMatrixToLoad(
        `matrixes/${module.config.baseWord.value}-L${
          module.config.settings.defaultLevel
        }.html?v=${Math.random()}`
      );
    });
  }, [baseWord]);
  const InitialArray = ["", "", "", "", ""];
  const [wordArray, setWordArray] = useState<WordArrayState>({
    values: InitialArray,
    leftRow: undefined,
    rightRow: undefined,
  });
  const [result, setResult] = useState(wordArray.values.join(""));
  const [builtWords, setBuiltWords] = useState<any>(new Set([]));
  const [sums, setSums] = useState<any>(new Set([]));
  const [activeButtons, setActiveButtons] = useState<ActiveButton[]>([]); // Track all active buttons (position + row)
  const [buttonPositions, setButtonPositions] = useState<number[]>([]);
  const [baseWordPosition, setBaseWordPosition] = useState<number>(2); // Default position

  const buttonsRef = useRef<HTMLButtonElement[]>([]); // Ref to store buttons for cleanup
  const wordArrayRef = useRef<WordArrayState>(wordArray);
  const activeButtonsRef = useRef<ActiveButton[]>(activeButtons);
  const [skillLevel, setSkillLevel] = useState<SkillLevel>(
    (`L${config.settings.defaultLevel}` as SkillLevel) || "L2"
  );
  const [showWordSum, setShowWordSum] = useState(true);
  const [showWordsBuilt, setShowWordsBuilt] = useState(true);
  const [tableHTML, setTableHTML] = useState<string | null>(null);
  const [matrixToLoad, setMatrixToLoad] = useState<RequestInfo | URL>(
    `matrixes/${baseWord}-${skillLevel}.html?v=${Math.random()}`
  );
  const [showSlides, setShowSlides] = useState<boolean>(false);

  let isWordValid;
  let validWordsCount = 0;
  if (skillLevel === "L1") {
    isWordValid = config?.L1ValidWords?.has(result);
    validWordsCount = config?.L1ValidWords?.size;
  }
  if (skillLevel === "L2") {
    isWordValid = config?.L2ValidWords?.has(result);
    validWordsCount = config?.L2ValidWords?.size;
  }
  if (skillLevel === "L3") {
    isWordValid = config?.L3ValidWords?.has(result);
    validWordsCount = config?.L3ValidWords?.size;
  }

  const isBaseWord = wordArray.values.join("") === baseWord;
  const noWordSelected = result === "";
  const wordStatus = isWordValid
    ? "success"
    : isBaseWord
    ? "default"
    : noWordSelected
    ? "default"
    : "danger";

  const userInitials =
    displayName &&
    displayName
      .split(" ")
      .map((name) => name.charAt(0))
      .join("");

  const getSide = (position: number) => {
    if (position < baseWordPosition) return "left";
    else if (position > baseWordPosition) return "right";
    else if (position === baseWordPosition) return "base"; // "baseword" button
    else return "unknown";
  };

  const handleClear = () => {
    handleClearWordsBuilt();
    handleClearResult();
    handleClearSum();
  };

  const handleClearResult = () => {
    setResult(InitialArray.join(""));
    setWordArray({
      values: InitialArray,
      leftRow: undefined,
      rightRow: undefined,
    });
    setActiveButtons([]);
  };

  const handleClearWordsBuilt = () => {
    setBuiltWords(new Set([]));
  };

  const handleClearSum = () => {
    handleClearWordsBuilt(); //add this here as words built trash icon was removed
    setSums(new Set([]));
  };

  const handleSetSkillLevel = (level: SkillLevel) => {
    handleClear();
    setSkillLevel(level);
    setMatrixToLoad(`matrixes/${baseWord}-${level}.html?v=${Math.random()}`);
  };

  const handleSetBasewordSelector = (word: string) => {
    handleClear();
    setBaseWord(word);
    setMatrixToLoad(`matrixes/${word}-${skillLevel}.html?v=${Math.random()}`);
  };

  const driverObj = driver(tour);

  const resetTourLocalStorage = () => {
    localStorage.removeItem("tour");
    startTourLocalStorage();
  };

  const startTourLocalStorage = () => {
    if (localStorage.getItem("tour") !== "seen") {
      driverObj.drive();
      localStorage.setItem("tour", "seen");
    }
  };

  const handleButtonClick = (e: Event) => {
    const button = e.currentTarget as HTMLButtonElement;
    const name = button.dataset.name!;
    const position = parseInt(button.dataset.position!);
    const row = parseInt(button.dataset.row!);

    if (!name || isNaN(position) || isNaN(row)) {
      console.error("Invalid button attributes.");
      return;
    }

    const side = getSide(position);

    // Use refs to get the latest state
    let newArray: WordArrayState = {
      values: [...wordArrayRef.current.values],
      leftRow: wordArrayRef.current.leftRow,
      rightRow: wordArrayRef.current.rightRow,
    };

    let updatedActiveButtons = [...activeButtonsRef.current];

    // Remove any active buttons in the same position (regardless of row)
    const previouslyActiveButtons = updatedActiveButtons.filter(
      (btn) => btn.position === position
    );
    updatedActiveButtons = updatedActiveButtons.filter(
      (btn) => btn.position !== position
    );

    if (side === "left" || position === baseWordPosition) {
      // **Toggle logic for left side and base word button**

      // Check if the clicked button is already active
      const isActive = activeButtonsRef.current.some(
        (btn) =>
          btn.position === position && btn.row === row && btn.name === name
      );

      if (!isActive) {
        updatedActiveButtons.push({ position, row, name });
      }
      // else: button was already active and has been removed from updatedActiveButtons
    } else if (side === "right") {
      // **Sequential activation enforced per row only if there is a previous button in the same row**

      // Check if there is a button at the previous position in the same row
      const hasPrevButtonInRow = buttonsRef.current.some(
        (btn) =>
          parseInt(btn.dataset.position!) === position - 1 &&
          parseInt(btn.dataset.row!) === row &&
          getSide(parseInt(btn.dataset.position!)) === "right"
      );

      let canActivate = true;

      if (hasPrevButtonInRow) {
        // Enforce sequential activation
        const isPrevActive = activeButtonsRef.current.some(
          (btn) =>
            btn.position === position - 1 &&
            btn.row === row &&
            getSide(btn.position) === "right"
        );

        if (!isPrevActive) {
          canActivate = false;
        }
      }

      if (!canActivate) {
        console.log(
          `Cannot activate position ${position} because position ${
            position - 1
          } in row ${row} is not active.`
        );
        return;
      }

      // **Toggle logic for right-side buttons**

      // Check if the clicked button is already active
      const isActive = activeButtonsRef.current.some(
        (btn) =>
          btn.position === position && btn.row === row && btn.name === name
      );

      if (isActive) {
        // Deactivate this button and all subsequent right-side buttons in the same row
        newArray = deactivateRightSideButtons(position, row, newArray);

        // Update activeButtons
        updatedActiveButtons = updatedActiveButtons.filter(
          (btn) =>
            !(
              getSide(btn.position) === "right" &&
              btn.position >= position &&
              btn.row === row
            )
        );
      } else {
        // Activate this button
        newArray = calculateNewArray(name, position, newArray);

        // Update activeButtons
        updatedActiveButtons.push({ position, row, name });
      }
    }

    // **Deactivate right-side buttons in affected rows where previous active buttons were removed**
    previouslyActiveButtons.forEach((prevBtn) => {
      if (prevBtn.row !== row) {
        // Deactivate right-side buttons in the previous row
        newArray = deactivateRightSideButtons(position, prevBtn.row, newArray);

        // Remove right-side buttons from activeButtons
        updatedActiveButtons = updatedActiveButtons.filter(
          (btn) =>
            !(
              getSide(btn.position) === "right" &&
              btn.position > position &&
              btn.row === prevBtn.row
            )
        );
      }
    });

    // **Find buttons that were removed and clear their values**
    const removedButtons = activeButtonsRef.current.filter(
      (btn) =>
        !updatedActiveButtons.some(
          (ubtn) =>
            ubtn.position === btn.position &&
            ubtn.row === btn.row &&
            ubtn.name === btn.name
        )
    );

    removedButtons.forEach((btn) => {
      newArray.values[btn.position] = "";
    });

    // **Set values for active buttons**
    updatedActiveButtons.forEach((btn) => {
      newArray.values[btn.position] = btn.name;
    });

    // Update refs
    wordArrayRef.current = newArray;
    activeButtonsRef.current = updatedActiveButtons;

    // **Set the wordArray and activeButtons state after updating**
    setWordArray(newArray);
    setActiveButtons(updatedActiveButtons);

    // Now update other state based on newArray
    let resultWord: string = newArray.values.join("");

    if (skillLevel === "L1") {
      const rule = config.L1WordsWithRulesApplied.find(
        (item: any) => item.search === resultWord
      );
      if (rule) {
        resultWord = rule.replaceWith;
      }
      if (config.L1ValidWords.has(resultWord)) {
        setBuiltWords(
          (prevBuiltWords: any) => new Set([...prevBuiltWords, resultWord])
        );
        setSums((prevSums: any) => {
          const newSum = newArray.values
            .filter((item: string) => item !== "")
            .join(" + ");
          return new Set([...prevSums, newSum]);
        });
      }
    }
    if (skillLevel === "L2") {
      const rule = config.L2WordsWithRulesApplied.find(
        (item: any) => item.search === resultWord
      );
      if (rule) {
        resultWord = rule.replaceWith;
      }
      if (config.L2ValidWords.has(resultWord)) {
        setBuiltWords(
          (prevBuiltWords: any) => new Set([...prevBuiltWords, resultWord])
        );
        setSums((prevSums: any) => {
          const newSum = newArray.values
            .filter((item: string) => item !== "")
            .join(" + ");
          return new Set([...prevSums, newSum]);
        });
      }
    }
    if (skillLevel === "L3") {
      const rule = config.L3WordsWithRulesApplied.find(
        (item: any) => item.search === resultWord
      );
      if (rule) {
        resultWord = rule.replaceWith;
      }
      if (config.L3ValidWords.has(resultWord)) {
        setBuiltWords(
          (prevBuiltWords: any) => new Set([...prevBuiltWords, resultWord])
        );
        setSums((prevSums: any) => {
          const newSum = newArray.values
            .filter((item: string) => item !== "")
            .join(" + ");
          return new Set([...prevSums, newSum]);
        });
      }
    }
    setResult(resultWord);
  };

  const calculateNewArray = (
    name: string,
    position: number,
    prevArray: WordArrayState
  ) => {
    const newArray = {
      ...prevArray,
      values: [...prevArray.values],
    };

    // **Toggle logic for any button**
    if (newArray.values[position] === name) {
      newArray.values[position] = "";
    } else {
      newArray.values[position] = name;
    }

    return newArray;
  };

  const deactivateRightSideButtons = (
    position: number,
    row: number,
    prevArray: WordArrayState
  ) => {
    const newArray = {
      ...prevArray,
      values: [...prevArray.values],
    };

    // Deactivate this button and all subsequent right-side buttons in the same row
    buttonPositions
      .filter((pos) => getSide(pos) === "right" && pos >= position)
      .forEach((pos) => {
        // Only clear the value if the active button is in the same row
        const isActiveInRow = activeButtonsRef.current.some(
          (btn) => btn.position === pos && btn.row === row
        );
        if (isActiveInRow) {
          newArray.values[pos] = "";
        }
      });

    return newArray;
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      startTourLocalStorage();
    }, 1000);

    // Return a cleanup function to clear the timeout
    return () => {
      clearTimeout(timeoutId);
    };
  });

  // Fetch the HTML content from the server when the component mounts
  useEffect(() => {
    const fetchTableHTML = async () => {
      try {
        const response = await fetch(matrixToLoad);
        if (response.ok) {
          const html = await response.text();
          setTableHTML(html);
        } else {
          console.error("Failed to fetch HTML:", response.status);
        }
      } catch (error) {
        console.error("Error fetching HTML:", error);
      }
    };

    fetchTableHTML();
  }, [matrixToLoad, skillLevel, baseWord]);

  useEffect(() => {
    // Cleanup function to remove event listeners from old buttons
    return () => {
      // Remove event listeners
      if (buttonsRef.current.length > 0) {
        buttonsRef.current.forEach((button) => {
          button.removeEventListener("click", handleButtonClick);
        });
        buttonsRef.current = [];
      }
    };
  }, [tableHTML, baseWord]);

  useEffect(() => {
    // Reset state variables and refs when tableHTML changes
    setActiveButtons([]);
    setWordArray({
      values: [],
      leftRow: undefined,
      rightRow: undefined,
    });

    wordArrayRef.current = {
      values: [],
      leftRow: undefined,
      rightRow: undefined,
    };
    activeButtonsRef.current = [];

    // Defer execution to allow DOM to update
    setTimeout(() => {
      const handleButtonsAvailable = () => {
        // Collect all buttons from the DOM
        const buttons = Array.from(
          document.querySelectorAll(".name-button")
        ) as HTMLButtonElement[];

        if (buttons.length === 0) {
          console.error("No buttons found in the DOM.");
          return;
        }

        // Get all positions
        const positions = buttons.map((button) =>
          parseInt(button.dataset.position || "0")
        );

        // **Determine the base word button's position and name**
        const baseWordBtn = buttons.find(
          (button) => button.dataset.name === baseWord
        );

        if (baseWordBtn) {
          const basePos = parseInt(baseWordBtn.dataset.position || "2");
          setBaseWordPosition(basePos);
        } else {
          // Handle the case where baseWord is not found
          console.error(`Base word "${baseWord}" not found among buttons.`);
          setBaseWordPosition(2); // Fallback to default position
        }

        // Update button positions
        setButtonPositions(positions);

        // Initialize wordArray.values with the correct length
        const maxPos = Math.max(...positions);
        setWordArray((prev) => ({
          ...prev,
          values: Array(maxPos + 1).fill(""),
        }));

        // Attach event listeners
        buttons.forEach((button) => {
          button.addEventListener("click", handleButtonClick);
        });

        // Store buttons in ref for cleanup
        buttonsRef.current = buttons;
      };

      handleButtonsAvailable();
    }, 0); // Defer execution until after DOM updates
  }, [tableHTML, baseWord]);

  // Update refs when state changes
  useEffect(() => {
    wordArrayRef.current = wordArray;
    activeButtonsRef.current = activeButtons;
  }, [wordArray, activeButtons]);

  // useEffect to update active class and disabled state based on activeButtons
  useEffect(() => {
    const buttons = Array.from(
      document.querySelectorAll(".name-button")
    ) as HTMLButtonElement[];

    buttons.forEach((button) => {
      const buttonPosition = parseInt(
        button.getAttribute("data-position") || "0"
      );
      const buttonRow = parseInt(button.getAttribute("data-row") || "0");
      const buttonName = button.getAttribute("data-name") || "";

      // Apply active class if button is in activeButtons array
      const isActive = activeButtons.some(
        (activeButton) =>
          activeButton.position === buttonPosition &&
          activeButton.row === buttonRow &&
          activeButton.name === buttonName
      );

      if (isActive) {
        button.classList.add("active");
      } else {
        button.classList.remove("active");
      }

      const side = getSide(buttonPosition);

      // **Disable logic for right-side buttons**
      if (side === "right") {
        // Check if there is a button at the previous position in the same row
        const hasPrevButtonInRow = buttonsRef.current.some(
          (btn) =>
            parseInt(btn.dataset.position!) === buttonPosition - 1 &&
            parseInt(btn.dataset.row!) === buttonRow &&
            getSide(parseInt(btn.dataset.position!)) === "right"
        );

        if (hasPrevButtonInRow) {
          // Enforce sequential activation
          const prevValue = activeButtons.find(
            (btn) =>
              btn.position === buttonPosition - 1 &&
              btn.row === buttonRow &&
              getSide(btn.position) === "right"
          );

          if (!prevValue) {
            button.setAttribute("disabled", "true");
          } else {
            button.removeAttribute("disabled");
          }
        } else {
          // No previous button in the same row; enable the button
          button.removeAttribute("disabled");
        }
      } else {
        // Left-side buttons and base word button are always enabled
        button.removeAttribute("disabled");
      }
    });
  }, [activeButtons, buttonPositions]);

  const handleUpgrade = () => {
    window.location.href = "https://www.morphologyhub.com/#pricing";
  };

  const handleBilling = () => {
    window.open(
      "https://www.morphologyhub.com/createCustomerPortalSession",
      "_blank"
    );
  };

  const handleLogout = () => {
    window.location.href = "https://www.morphologyhub.com/sign-out";
  };

  return (
    <>
      <div className="flex h-screen">
        <div className="grid grid-cols-[220px_1fr]">
          <div className="bg-white border-r border-gray-300">
            {/* navbar start */}
            <div className="flex flex-1 flex-col justify-between h-full pb-11 px-4">
              <div>
                {/* title */}
                <h1 className="text-2xl font-bold tracking-tight pt-4 pb-8 rose-pink">
                  <a href="/">Morphology Hub</a>
                </h1>
                {/* baseword */}
                <div
                  className="flex grow flex-row items-center justify-between pb-4 gap-2"
                  id="baseword"
                >
                  <div className="space-y-0.5">
                    <div className="text-sm">Base</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Select
                      onValueChange={handleSetBasewordSelector}
                      defaultValue={"sign"}
                      value={baseWord}
                    >
                      <SelectTrigger className="w-[85px]">
                        <SelectValue placeholder="Baseword" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableBasewords.map((baseword) =>
                          baseword.isLocked ? (
                            <SelectItemLocked
                              key={baseword.value}
                              value={baseword.value}
                              disabled
                            >
                              {baseword.label}
                            </SelectItemLocked>
                          ) : (
                            <SelectItem
                              key={baseword.value}
                              value={baseword.value}
                            >
                              {baseword.label}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* level */}
                <div
                  className="flex grow flex-row items-center justify-between pb-4 gap-2"
                  id="level"
                >
                  <div className="space-y-0.5">
                    <div className="text-sm">Level</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Select
                      onValueChange={handleSetSkillLevel}
                      defaultValue={"L2"}
                      value={skillLevel}
                    >
                      <SelectTrigger className="w-[85px]">
                        <SelectValue placeholder="Level" />
                      </SelectTrigger>
                      <SelectContent>
                        {config?.settings?.availableLevels.map((level: any) => (
                          <SelectItem value={`L${level}`} key={level}>
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* clear all */}
                <div
                  className="flex grow flex-row items-center justify-between pb-4 gap-2"
                  id="clear_all"
                >
                  <div className="space-y-0.5">
                    <div className="text-sm">Clear all</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="icon" onClick={handleClear}>
                      <Trash2Icon className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>

                {/* slides */}
                <div
                  className="flex grow flex-row items-center justify-between pb-4 gap-2"
                  id="slides"
                >
                  <div className="space-y-0.5">
                    <div className="text-sm">Lesson deck</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowSlides(!showSlides)}
                    >
                      {showSlides ? (
                        <PauseIcon className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <PlayIcon className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                {/* rules */}
                <div
                  className="flex grow flex-row items-center justify-between pb-4 gap-2"
                  id="rules"
                >
                  <div className="space-y-0.5">
                    <div className="text-sm">Rules</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="icon">
                          <ScrollTextIcon className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-xl">
                            Matrix rules
                          </DialogTitle>
                          <DialogDescription className="text-md">
                            <ul className="list-disc ml-4 text-black">
                              <li>Work from left to right</li>
                              <li>You must use the base</li>
                              <li>You may not skip over a column</li>
                              <li>You do not have to use all the columns</li>
                              <li>
                                Joining rules may apply when using more than one
                                morpheme
                              </li>
                            </ul>
                          </DialogDescription>
                          <DialogTitle className="text-xl pt-4">
                            If unsure - ask yourself
                          </DialogTitle>
                          <DialogDescription className="text-md">
                            <ul className="list-disc ml-4 text-black">
                              <li>Can I use it in a sentence?</li>
                              <li>What might it mean?</li>
                              <li>
                                Click on the words built to look them up on
                                Merriam Webster dictionary
                              </li>
                            </ul>
                          </DialogDescription>
                          <DialogTitle className="text-xl pt-4">
                            The big 3
                          </DialogTitle>
                          <DialogDescription className="text-md">
                            <ul className="list-disc ml-4 text-black">
                              <li>The doubling rule</li>
                              <li>
                                Drop the final vowel to add a vowel suffix
                              </li>
                              <li>The y rule</li>
                            </ul>
                          </DialogDescription>
                        </DialogHeader>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                <Accordion
                  type="single"
                  collapsible
                  className="w-full"
                  id="printables"
                >
                  <AccordionItem value="item-1">
                    <AccordionTrigger>Print resources</AccordionTrigger>
                    <AccordionContent>
                      <ul>
                        {config?.resources.map(
                          (resource: any, index: number) => (
                            <li
                              className={`border-b mb-1 ${
                                index === config.resources.length - 1
                                  ? "border-b-0"
                                  : ""
                              }`}
                              key={index}
                            >
                              <a
                                href={resource.url}
                                className="hover:underline"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {resource.name}
                              </a>
                            </li>
                          )
                        )}
                        {/* <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            All three levels list
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 1 list only
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 2 list only
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 3 list only
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Word additions to each level list
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 1 reading passage
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 2 reading passage
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 3 reading passage
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 1 copy passage
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 2 copy passage
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 3 copy passage
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 1 cloze passage
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 2 cloze passage
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Level 3 cloze passage
                          </a>
                        </li>
                        <li className="border-b mb-1">
                          <a href="#" className="hover:underline">
                            Analytic word sums worksheet
                          </a>
                        </li>
                        <li>
                          <a href="#" className="hover:underline">
                            Synthetic word sums worksheet
                          </a>
                        </li> */}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <hr />

                {/* settings */}
                <div className="flex flex-wrap gap-1 mb-2">
                  {/* show word sum */}
                  <div
                    className="flex grow flex-row items-center justify-between mt-4 pb-4 gap-2"
                    id="settings1"
                  >
                    <div className="space-y-0.5">
                      <div className="text-sm">Show word sum</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="showWordSum"
                        checked={showWordSum}
                        onCheckedChange={setShowWordSum}
                      />
                    </div>
                  </div>
                  {/* show words built */}
                  <div
                    className="flex grow flex-row items-center justify-between pb-4 gap-2"
                    id="settings2"
                  >
                    <div className="space-y-0.5">
                      <div className="text-sm">Show words built</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="showWordsBuilt"
                        checked={showWordsBuilt}
                        onCheckedChange={setShowWordsBuilt}
                      />
                    </div>
                  </div>
                </div>

                {/* help */}
                <div className="flex flex-wrap gap-1 mb-2" id="help">
                  <div className="flex grow flex-row items-center justify-between gap-2">
                    <div className="space-y-0.5">
                      <div className="text-sm">Help</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={resetTourLocalStorage}
                      >
                        <HelpCircleIcon className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                {isLoggedIn ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full p-2">
                        <Avatar className="h-8 w-8 rounded-xs">
                          <AvatarFallback className="rounded-xs">
                            <UserRound />
                          </AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1 text-left text-sm leading-tight ml-2">
                          <span className="truncate font-semibold">
                            {userInitials}
                          </span>
                        </div>
                        <ChevronsUpDown className="ml-auto size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                      side="bottom"
                      align="end"
                      sideOffset={4}
                    >
                      <DropdownMenuLabel className="p-0 font-normal">
                        <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                          <Avatar className="h-8 w-8 rounded-xs">
                            <AvatarFallback className="rounded-xs">
                              <UserRound />
                            </AvatarFallback>
                          </Avatar>
                          <div className="grid flex-1 text-left text-sm leading-tight">
                            <span className="truncate font-semibold">
                              {userInitials}
                            </span>
                          </div>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup></DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        {/* <DropdownMenuItem>
                          <BadgeCheck />
                          Account
                        </DropdownMenuItem> */}
                        <DropdownMenuItem
                          onClick={handleBilling}
                          className="cursor-pointer"
                        >
                          <CreditCard className="mr-2" />
                          Billing
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer"
                      >
                        <LogOut className="mr-2" />
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <>
                    <Card className="shadow-none border p-2 pink-border">
                      <CardHeader className="p-0">
                        <CardTitle className="text-md text-center">
                          Ready to upgrade?
                        </CardTitle>
                        <CardDescription>
                          Get full access to all matrices and features for just
                          $15 USD per month.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="p-0 pt-4">
                        <Button
                          variant="outline"
                          className="pink w-full shadow-none border-none text-sm text-white"
                          size="sm"
                          onClick={handleUpgrade}
                        >
                          Upgrade
                        </Button>
                      </CardContent>
                    </Card>
                    <div className="mt-4 text-center">
                      <p className="text-sm text-muted-foreground">
                        <a href="/sign-in" className="hover:underline">
                          Already have an account?
                        </a>
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* navbar end */}
          </div>
          <div className="grid grid-rows-[1fr_auto]">
            <div className="grid grid-cols-1 xl:grid-cols-[auto_1fr] gap-4 p-4">
              {tableHTML ? (
                <div
                  className={`matrix-level-${skillLevel}`}
                  dangerouslySetInnerHTML={{ __html: tableHTML }}
                />
              ) : (
                <p>Loading matrix...</p>
              )}
              <div className="grid grid-rows-[160px_1fr]">
                <Card
                  className={
                    wordStatus === "danger"
                      ? "h-40 border-2 border-red-500 grid grid-rows-[50px_1fr]"
                      : wordStatus === "success"
                      ? "h-40 border-2 border-green-500 grid grid-rows-[50px_1fr]"
                      : "h-40 border-2 grid grid-rows-[50px_1fr]"
                  }
                  id="result"
                >
                  <CardHeader className="bg-muted/50 px-6 py-2">
                    <CardTitle className="flex justify-between items-center">
                      <div className="text-lg">Result</div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleClearResult}
                        id="result_clear"
                      >
                        <Trash2Icon className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 text-sm flex items-center justify-center">
                    <h2 className="text-4xl text-center font-semibold leading-none tracking-tight">
                      <span
                        className={wordStatus === "danger" ? "wavyRed" : ""}
                      >
                        {result}
                      </span>
                    </h2>
                  </CardContent>
                </Card>

                <div className="mt-4">
                  {showSlides ? (
                    <div className="w-full max-w-3xl">
                      <Slideshow
                        directory={`images/slides/${baseWord}/`}
                        slideCount={config.settings.slideCount}
                        showSlideCount={true}
                        showNavigationButtons={false}
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {showWordSum && (
                        <Card
                          className="w-full border-2 border-gray-200"
                          id="sum"
                        >
                          <CardHeader className="bg-muted/50 px-6 py-3">
                            <div className="flex justify-between items-center">
                              <CardTitle className="text-md">Sum</CardTitle>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleClearSum}
                                id="clear_sum_button"
                              >
                                <Trash2Icon className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="p-6 text-md">
                            <ul>
                              {Array.from(sums).map((word, index) => (
                                <li key={index}>{word as string} &rarr;</li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      )}
                      {showWordsBuilt && (
                        <Card
                          className="w-full border-2 border-gray-200"
                          id="words_built"
                        >
                          <CardHeader className="bg-muted/50 px-6 pt-5 pb-4">
                            <div className="w-full flex justify-between relative">
                              <CardTitle className="text-md mr-2">
                                Words built
                              </CardTitle>

                              <div id="words_built_counter">
                                {builtWords.size === validWordsCount && (
                                  <div className="absolute right-0">
                                    <ConfettiExplosion />
                                  </div>
                                )}
                                <Badge
                                  variant={
                                    builtWords.size === validWordsCount
                                      ? "destructive"
                                      : "outline"
                                  }
                                >
                                  {builtWords.size}/{validWordsCount}
                                </Badge>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="p-6 text-md">
                            <ul>
                              {Array.from(builtWords).map((word, index) => (
                                <li key={index}>
                                  <a
                                    href={`https://www.merriam-webster.com/dictionary/${word}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hover:underline new-window-link"
                                  >
                                    {word as string}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-end p-4">
              <p className="text-sm text-muted-foreground pt-4 border-t-2">
                &copy; 2024-2025 Sarah's Literacy Lab. All rights reserved.{" "}
                <a href="https://morphologyhub.com">morphologyhub.com</a>{" "}
                features interactive tools and resources designed to enhance the
                teaching and learning experience. The content and materials
                provided herein are protected by copyright law and may not be
                reproduced, distributed, or modified without express written
                permission. We kindly ask you to consider the time, expertise,
                and resources invested in creating these educational materials
                which are offered at a low cost to ensure accessibility for all
                users. Your respect for our intellectual property helps us
                continue to provide high-quality resources for educational
                purposes. Thank you for your understanding and support.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;
